import React from 'react';
import { experienceQrUrl } from '@/services/experience.service';
import { Badge } from '@/components/ui/badge';
import { Sparkles, CalendarDays, MapPin, CheckCircle2 } from 'lucide-react';

function fmtDate(iso) {
  if (!iso) return 'Data da definire';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('it-IT', { weekday: 'short', day: '2-digit', month: 'short' }) + ' · ' +
      d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
}

/**
 * Biglietto digitale premium — stile "boarding pass" con perforazione, QR e stato.
 * Coerente col PDF generato lato server.
 */
export function ExperienceTicketCard({ ticket, experience, sessionStart }) {
  const validated = !!ticket.checked_in;
  const loc = experience?.location?.name;
  return (
    <div
      className="relative overflow-hidden rounded-2xl border border-border bg-card shadow-sm"
      data-testid={`ticket-qr-${ticket.code}`}
    >
      {/* Header brand */}
      <div className="flex items-center justify-between bg-primary px-4 py-2.5 text-primary-foreground">
        <span className="flex items-center gap-1.5 text-sm font-semibold tracking-tight">
          <Sparkles className="h-4 w-4" />Sila Experiences
        </span>
        <span className="rounded-full bg-white/15 px-2.5 py-0.5 text-xs font-medium">
          {ticket.ticket_type || 'Ingresso'}
        </span>
      </div>

      <div className="flex items-stretch">
        {/* Dettagli */}
        <div className="min-w-0 flex-1 p-4">
          <h4 className="truncate font-display text-base font-semibold text-foreground">
            {experience?.title || 'Esperienza'}
          </h4>
          <p className="mt-1.5 flex items-center gap-1.5 text-xs text-muted-foreground">
            <CalendarDays className="h-3.5 w-3.5 text-primary" />{fmtDate(sessionStart)}
          </p>
          {loc && (
            <p className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground">
              <MapPin className="h-3.5 w-3.5 text-primary" />{loc}
            </p>
          )}
          <div className="mt-3 inline-flex flex-col rounded-lg bg-muted px-3 py-1.5">
            <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">Codice biglietto</span>
            <span className="font-mono text-sm font-semibold text-foreground">{ticket.code}</span>
          </div>
          <div className="mt-3">
            {validated ? (
              <Badge className="gap-1 border-0 bg-emerald-500/15 text-emerald-600 dark:text-emerald-400">
                <CheckCircle2 className="h-3 w-3" />Validato
              </Badge>
            ) : (
              <Badge variant="secondary" className="border-0 bg-muted text-muted-foreground">Non ancora validato</Badge>
            )}
          </div>
        </div>

        {/* Perforazione */}
        <div className="relative w-px self-stretch">
          <div className="absolute inset-y-4 left-1/2 -translate-x-1/2 border-l-2 border-dashed border-border" />
          <span className="absolute -top-2 left-1/2 h-4 w-4 -translate-x-1/2 rounded-full border border-border bg-background" />
          <span className="absolute -bottom-2 left-1/2 h-4 w-4 -translate-x-1/2 rounded-full border border-border bg-background" />
        </div>

        {/* QR */}
        <div className="flex w-32 shrink-0 flex-col items-center justify-center p-3">
          <div className="rounded-xl border border-border bg-white p-1.5">
            <img src={experienceQrUrl(ticket.code)} alt={`QR ${ticket.code}`} className="h-24 w-24" />
          </div>
          <span className="mt-1.5 text-center text-[10px] text-muted-foreground">Inquadra all'ingresso</span>
        </div>
      </div>

      {validated && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <span className="rotate-[-14deg] rounded-lg border-2 border-emerald-500/40 px-4 py-1 text-lg font-black uppercase tracking-widest text-emerald-500/40">
            Validato
          </span>
        </div>
      )}
    </div>
  );
}
